import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "flaneriet:hide-paywall:v1";

const listeners = new Set<() => void>();

function readFromStorage(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return true;
    return raw === "1";
  } catch {
    return true;
  }
}

// Cached snapshot so useSyncExternalStore gets a stable reference between reads.
let currentSnapshot: boolean | null = null;

function getSnapshot(): boolean {
  if (currentSnapshot === null) {
    currentSnapshot = readFromStorage();
  }
  return currentSnapshot;
}

function getServerSnapshot(): boolean {
  return true;
}

function emit() {
  for (const listener of listeners) listener();
}

function setHidePaywall(next: boolean) {
  if (currentSnapshot === next) return;
  currentSnapshot = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  } catch {
    /* noop */
  }
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  // Sync across tabs
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return;
    const next = event.newValue === null ? true : event.newValue === "1";
    if (currentSnapshot !== next) {
      currentSnapshot = next;
      emit();
    }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    listeners.delete(listener);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

export function usePaywallFilter() {
  const hidePaywall = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const toggle = useCallback(() => {
    setHidePaywall(!getSnapshot());
  }, []);

  return { hidePaywall, toggle };
}
